import React, { useState, useCallback, useEffect } from 'react';
import './AIGeneratePanel.css';

/**
 * Props for AIGeneratePanel component
 */
export interface AIGeneratePanelProps {
  /** Callback when generate is requested */
  onGenerate: (prompt: string) => void;
  /** Whether generation is in progress */
  isGenerating: boolean;
  /** Current loading message */
  loadingMessage?: string;
  /** Progress percentage (0-100) */
  progress?: number;
  /** Error message to display */
  errorMessage?: string;
  /** Callback when panel is closed */
  onClose: () => void;
}

/**
 * AIGeneratePanel Component
 * 
 * Full-screen panel for AI music generation on mobile.
 * Provides prompt input and generate button, with loading state.
 */
export const AIGeneratePanel: React.FC<AIGeneratePanelProps> = ({
  onGenerate,
  isGenerating,
  loadingMessage = 'Generating...',
  progress = 0,
  errorMessage,
  onClose,
}) => {
  const [prompt, setPrompt] = useState('');

  /**
   * Handle generate button click
   */
  const handleGenerate = useCallback(() => {
    if (!prompt.trim() || isGenerating) return;
    onGenerate(prompt.trim());
  }, [prompt, isGenerating, onGenerate]);

  /**
   * Handle prompt change
   */
  const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  }, []);

  /**
   * Clear prompt after successful generation
   */
  useEffect(() => {
    if (!isGenerating && progress === 100) {
      setPrompt('');
    }
  }, [isGenerating, progress]);

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="ai-generate-panel-backdrop" 
        onClick={!isGenerating ? onClose : undefined}
        aria-hidden="true"
      />
      
      {/* Panel container */}
      <div 
        className="ai-generate-panel" 
        role="dialog" 
        aria-label="AI music generation"
        aria-modal="true"
      >
        {/* Header with close button */}
        <div className="ai-generate-panel-header">
          <h2 className="ai-generate-panel-title">
            <i className="fa-solid fa-sparkles"></i>
            AI Generate
          </h2>
          {!isGenerating && (
            <button
              className="ai-generate-panel-close"
              onClick={onClose}
              aria-label="Close AI generation"
              title="Close"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}
        </div>

        {/* Content area */}
        <div className="ai-generate-panel-content">
          {isGenerating ? (
            // Loading State
            <div className="ai-generate-loading">
              {/* Loading Message */}
              <div className="ai-generate-loading-message">
                <span className="ai-generate-loading-icon">🎮</span>
                <span className="ai-generate-loading-text">{loadingMessage}</span>
              </div>

              {/* Progress Bar */}
              <div className="ai-generate-progress-container">
                <div 
                  className="ai-generate-progress-bar"
                  style={{ width: `${progress}%` }}
                >
                  <div className="ai-generate-progress-scanline" />
                </div>
                <div className="ai-generate-progress-text">
                  {Math.round(progress)}%
                </div>
              </div>

              {/* Loading Tip */}
              <div className="ai-generate-loading-tip">
                ⚡ PLEASE WAIT - DO NOT CLOSE ⚡
              </div>
            </div>
          ) : (
            // Input State
            <>
              {/* Instructions */}
              <div className="ai-generate-instructions">
                <p>Describe the type of 8-bit music you want to create:</p>
              </div>

              {/* Prompt Input */}
              <textarea
                className="ai-generate-prompt"
                rows={6}
                value={prompt}
                onChange={handlePromptChange}
                placeholder="e.g., 'upbeat battle theme' or 'peaceful village music'..."
                aria-label="Music generation prompt"
                autoFocus
              />

              {/* Error Message */}
              {errorMessage && (
                <div className="ai-generate-error">
                  <i className="fa-solid fa-circle-exclamation"></i>
                  {errorMessage}
                </div>
              )}

              {/* Examples */}
              <div className="ai-generate-examples">
                <div className="ai-generate-examples-title">Examples:</div>
                <button
                  className="ai-generate-example-chip"
                  onClick={() => setPrompt('upbeat battle theme with fast drums')}
                  type="button"
                >
                  Battle Theme
                </button>
                <button
                  className="ai-generate-example-chip"
                  onClick={() => setPrompt('peaceful village music with soft melody')}
                  type="button"
                >
                  Village Music
                </button>
                <button
                  className="ai-generate-example-chip"
                  onClick={() => setPrompt('mysterious dungeon theme with dark tones')}
                  type="button"
                >
                  Dungeon Theme
                </button>
              </div>

              {/* Generate Button */}
              <button
                className="ai-generate-button"
                onClick={handleGenerate}
                disabled={!prompt.trim()}
                aria-label="Generate music from prompt"
              >
                <i className="fa-solid fa-wand-magic-sparkles"></i>
                Generate Music
              </button>

              {/* Helper Text */}
              <div className="ai-generate-helper">
                💡 Tip: Be specific about mood, tempo, and instruments for best results
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
